import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  Box,
  Slider,
  IconButton,
  Chip,
  ListItemText,
  CircularProgress,
  Tooltip,
  Alert,
} from "@mui/material";
import { 
  Close, 
  Refresh, 
  OpenInNew, 
  Remove, 
  ExpandMore, 
  ExpandLess, 
  Cancel,
  HelpOutline,
  CheckCircle,
  Warning,
} from "@mui/icons-material";
import { useTheme } from "../../context/ThemeContext";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import folderService from "../../services/folderService";
import resourceSharingService from "../../services/resourceSharingService";
import kgDatasetService from "../../services/kgDatasetService";
import { unwrapList as unwrapKgList } from "../KnowledgeGraph/kgUtils";
import { HelpCircle } from "lucide-react";
import {
  KnowledgeBaseRecencyFields,
  applyRecencyPatch,
  renderRetrievalTypeOptions,
} from "./KnowledgeBaseRetrievalFields";
import { SHARED_KB_COPY } from "../../constants/sharedKnowledgeCopy";
import styles from "./KnowledgeBaseDialog.module.scss";

// `personal` turns the dialog into a share recipient's own folder picker for a
// shared agent: `onSave` is awaited (it writes `/diy-agents/{id}/my-dim3`, which
// can refuse a folder), and folders that only reached the user as an attachment
// of a shared agent are shown but cannot be picked.
const KnowledgeBaseD3Dialog = ({ open, onClose, onSave, initialData, hiddenFolderIds, personal = false }) => {
  const { isDarkMode } = useTheme();

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: "#e36250 !important",
      },
      secondary: {
        main: "#8b5cf6",
      },
    },
  });

  // Compatibility matrix for search types and retrieval methods
  const compatibilityMatrix = {
    "Vector Search": {
      "Basic": { compatible: true, description: "Pure semantic similarity search" },
      "Hybrid": { compatible: true, description: "Semantic similarity combined with keyword matching" },
      "MMR": { compatible: true, description: "Semantic similarity with diversity selection" },
      "HyDE": { compatible: true, description: "Hypothetical document embeddings for better retrieval" }
    },
    "Hybrid Search": {
      "Basic": { compatible: true, description: "Combined vector + keyword search" },
      "Hybrid": { compatible: true, description: "Meaning-based and keyword search combined" },
      "MMR": { compatible: true, description: "Hybrid search with diversity selection" },
      "HyDE": { compatible: true, description: "Hybrid search with hypothetical documents" }
    },
    "Keyword Search": {
      "Basic": { compatible: true, description: "Text-based search only" },
      "Hybrid": { compatible: false, description: "No embeddings for meaning-based search" },
      "MMR": { compatible: false, description: "No embeddings for similarity calculations" },
      "HyDE": { compatible: false, description: "No embeddings for hypothetical documents" }
    }
  };

  // Folder ids arrive as numbers from some APIs (shared folders use the share's
  // resource_id) and as strings from others / from ids persisted in the saved
  // agent JSON — never compare them strictly. Match loosely here, then reuse the
  // folder's own id so MUI's strict Select value matching still works.
  const findFolderById = (id) =>
    folders.find((folder) => String(folder.id) === String(id));

  // Helper function to get available folders for a specific knowledge base
  const getAvailableFolders = (currentKbId) => {
    // Built inside this function, not at component scope: `kgFolderIds` is
    // declared further down, so reading it during the render body would hit the
    // temporal dead zone and throw.
    // A recipient's own selection is offered every folder they own: it has no
    // Knowledge Graph card to send a dataset folder to instead.
    const hiddenFolderIdSet = personal ? new Set() : new Set([
      ...(Array.isArray(hiddenFolderIds) ? hiddenFolderIds : []).map((id) => String(id)),
      ...kgFolderIds,
    ]);
    return folders.filter(folder => {
      // Folders owned by a Knowledge Graph dataset, or already attached via the
      // KG feature card: offering them here would attach one twice, or attach it
      // without the knowledge_graph flag and silently drop the KG tools.
      if (hiddenFolderIdSet.has(String(folder.id))) return false;
      // Filter out folders that are already selected in other knowledge bases
      const isSelectedInOtherKB = formData.knowledgeBases
        .filter(otherKb => otherKb.id !== currentKbId)
        .some(otherKb =>
          Array.isArray(otherKb.knowledgeBase) &&
          otherKb.knowledgeBase.some((selectedId) => String(selectedId) === String(folder.id))
        );
      return !isSelectedInOtherKB;
    });
  };

  // Helper component for detailed tooltips
  const InfoTooltip = ({ title, content, children, placement = "top", maxWidth = 1500 }) => (
    <Tooltip
      title={
        <Box sx={{ 
          width: '100%',
          maxHeight: '400px',
          overflowY: 'auto',
          p: 1,
          boxSizing: "border-box",
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.5)',
            },
          },
        }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: "white" }}
          >
            {title}
          </Typography>
          {typeof content === "string" ? (
            <Typography variant="body2" sx={{ 
              color: "rgba(255,255,255,0.9)",
              wordWrap: "break-word",
              overflowWrap: "break-word"
            }}>
              {content}
            </Typography>
          ) : (
            content
          )}
        </Box>
      }
      placement={placement}
      arrow
      enterDelay={300}
      leaveDelay={200}
      sx={{
        "& .MuiTooltip-tooltip": {
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          fontSize: "0.875rem",
          maxWidth: `${maxWidth}px !important`,
          padding: "12px 16px",
          wordWrap: "break-word",
          overflowWrap: "break-word",
          whiteSpace: "normal",
          boxSizing: "border-box",
        },
        "& .MuiTooltip-arrow": {
          color: "rgba(0, 0, 0, 0.9)",
        },
      }}
    >
      {children}
    </Tooltip>
  );

  // Check compatibility for a knowledge base
  const checkCompatibility = (kb) => {
    if (!kb.knowledgeBase || !Array.isArray(kb.knowledgeBase) || kb.knowledgeBase.length === 0) {
      return { compatible: true, message: "" };
    }

    // Get search type from the first selected folder
    const firstFolder = findFolderById(kb.knowledgeBase[0]);
    if (!firstFolder) {
      return { compatible: true, message: "" };
    }

    // Map the searchType from the folder to the compatibility matrix keys
    // The searchType is nested in processingOptions based on the data structure
    let searchType = firstFolder.processingOptions?.searchType || firstFolder.searchType || "hybrid";
    
    // Normalize the search type to match the compatibility matrix
    if (searchType.toLowerCase() === "keyword") {
      searchType = "Keyword Search";
    } else if (searchType.toLowerCase() === "vector") {
      searchType = "Vector Search";
    } else if (searchType.toLowerCase() === "hybrid") {
      searchType = "Hybrid Search";
    } else {
      // Default to Hybrid Search if unknown
      searchType = "Hybrid Search";
    }
    
    // Debug logging to help troubleshoot
    console.log('D3 Folder data:', firstFolder);
    console.log('D3 Extracted searchType:', searchType);
    console.log('D3 Retrieval type:', kb.retrievalType);
    
    const retrievalType = kb.retrievalType || "Basic";
    
    const compatibility = compatibilityMatrix[searchType]?.[retrievalType];
    
    if (!compatibility) {
      return { compatible: true, message: "" };
    }

    if (!compatibility.compatible) {
      return {
        compatible: false,
        message: `Incompatible: ${searchType} + ${retrievalType}. ${compatibility.description}`
      };
    }

    return {
      compatible: true,
      message: `Compatible: ${searchType} + ${retrievalType}. ${compatibility.description}`
    };
  };

  const safeInitialData = initialData || {};
  const [formData, setFormData] = useState({
    ragType: safeInitialData.ragType || "RAG",
    knowledgeBases: safeInitialData.knowledgeBases || [],
  });
  useEffect(() => {
    if (open) {
      // Reset form data when dialog opens with new initialData
      const ragType = safeInitialData.ragType || "RAG";
      const knowledgeBases = safeInitialData.knowledgeBases || [];
      
      // If RAG mode and no knowledge bases, create a default one
      if (ragType === "RAG" && knowledgeBases.length === 0) {
        knowledgeBases.push({
          id: Date.now(),
          knowledgeBase: [],
          numberOfChunks: 5,
          retrievalType: "Basic",
          scoreThreshold: 0.0,
        });
      }
      
      setFormData({
        ragType,
        knowledgeBases,
      });

      // Reset errors
      setErrors({
        knowledgeBase: false,
        numberOfChunks: false,
      });
      setSaveError(null);
    }
  }, [open, initialData, safeInitialData.ragType, safeInitialData.knowledgeBases]);

  const [errors, setErrors] = useState({
    knowledgeBase: false,
    numberOfChunks: false,
    compatibility: false,
  });
  // Personal mode only: the save is a request of its own and can be refused.
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [collapsedKBs, setCollapsedKBs] = useState(new Set());
  
  // State for knowledge base folders
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [foldersError, setFoldersError] = useState(null);
  const [kgFolderIds, setKgFolderIds] = useState([]);

  // Load D3 knowledge base folders from API
  const loadFolders = useCallback(async () => {
    setLoadingFolders(true);
    setFoldersError(null);
    try {
      const [response, sharedResponse, kgResponse] = await Promise.all([
        // Listing own folders needs the kb_dim3 permission (403 without it). A
        // recipient without it has no folders of their own, but can still pick
        // from the folders shared with them.
        folderService.getFolders('d3_knowledge_base').catch((error) => {
          if (personal) return [];
          throw error;
        }),
        resourceSharingService.getSharedWithMe('folder_dim3').catch(() => ({ items: [] })),
        // Folders owned by a Knowledge Graph dataset. Picking one here attaches
        // it WITHOUT turning on `features.knowledge_graph`, so the agent gets
        // the records as plain RAG and none of the filter or count tools — a
        // silent downgrade. They belong to the Knowledge Graph feature card.
        kgDatasetService.listDatasets().catch(() => null),
      ]);
      setKgFolderIds(
        unwrapKgList(kgResponse, 'datasets', 'kg_datasets')
          .map((d) => d?.folder_id)
          .filter((id) => id !== undefined && id !== null)
          .map(String)
      );
      let shared = Array.isArray(sharedResponse?.items) ? sharedResponse.items.map((item) => ({
        ...(item.resource || {}), id: item.resource_id ?? item.resource?.id,
        owner_user_id: item.owner?.id, __shared: true, __shareOwner: item.owner,
        // Reached the user as an attachment of a shared agent, not as a folder
        // share of its own.
        __viaAgentShare: item.parent_share_id != null,
      })) : [];
      if (personal) {
        // A folder can arrive both ways. The direct share is the usable one, so
        // it wins over the attachment row.
        const byId = new Map();
        shared.forEach((folder) => {
          const key = String(folder.id);
          const existing = byId.get(key);
          if (!existing || (existing.__viaAgentShare && !folder.__viaAgentShare)) byId.set(key, folder);
        });
        shared = [...byId.values()];
      }
      setFolders([...(Array.isArray(response) ? response : []), ...shared]);
    } catch (error) {
      console.error('Error loading D3 knowledge base folders:', error);
      setFoldersError('Failed to load D3 knowledge base folders');
      setFolders([]);
    } finally {
      setLoadingFolders(false);
    }
  }, [personal]);

  // Load folders when dialog opens
  useEffect(() => {
    if (open) {
      loadFolders();
    }
  }, [open, loadFolders]);

  // The backend refuses a folder that only reached the user as an attachment of
  // a shared agent — this is what keeps the agent owner's D3 folders out of a
  // recipient's selection.
  const isFolderUnusable = (folder) => personal && Boolean(folder?.__viaAgentShare);

  // All selected folders must use one embedding model, or the save is a 400.
  // Once another entry holds a folder, folders on a different model are
  // disabled for this entry. Folders that do not say their model stay enabled
  // and are left to the backend.
  const hasEmbeddingMismatch = (folder, currentKbId) => {
    if (!personal || !folder?.embeddingModel) return false;
    const otherModels = formData.knowledgeBases
      .filter((kb) => kb.id !== currentKbId && Array.isArray(kb.knowledgeBase) && kb.knowledgeBase.length > 0)
      .map((kb) => findFolderById(kb.knowledgeBase[0])?.embeddingModel)
      .filter(Boolean);
    return otherModels.length > 0 && otherModels[0] !== folder.embeddingModel;
  };

  // Helper function to render folder information
  const renderFolderInfo = (folder, currentKbId) => {
    const sharedBy = folder.__shareOwner?.name || folder.__shareOwner?.email || 'teammate';
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {folder.name}
        </Typography>
        {folder.__shared && <Chip label={personal ? SHARED_KB_COPY.sharedFolderTag(sharedBy) : `Shared by ${sharedBy}`} size="small" color="secondary" sx={{ alignSelf: 'flex-start' }} />}
        {/* A disabled MenuItem swallows hover, so the reason is spelled out. */}
        {!isFolderUnusable(folder) && hasEmbeddingMismatch(folder, currentKbId) && (
          <Typography variant="caption" color="text.secondary">
            {SHARED_KB_COPY.embeddingDiffers}
          </Typography>
        )}
        {isFolderUnusable(folder) && (
          <Typography variant="caption" color="text.secondary">
            {SHARED_KB_COPY.folderNotUsable}
          </Typography>
        )}
        {folder.description && (
          <Typography variant="caption" color="text.secondary">
            {folder.description}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
          <Chip
            label={`Vector Store: ${folder.vectorStore || 'PGVector'}`}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 16 }}
          />
          <Chip
            label={`Embedding: ${folder.embeddingModel || 'text-embedding-3-large'}`}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 16 }}
          />
        </Box>
      </Box>
    );
  };


  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };
      
      // If switching to RAG mode, keep only the first knowledge base
      if (field === "ragType" && value === "RAG") {
        newData.knowledgeBases = prev.knowledgeBases.length > 0 ? [prev.knowledgeBases[0]] : [];
      }
      // If switching to Agentic RAG mode, keep all knowledge bases as they are
      
      return newData;
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: false,
      }));
    }
  };

  const handleAddKnowledgeBase = () => {
    const newKB = {
      id: Date.now(),
      knowledgeBase: [],
      numberOfChunks: 5,
      retrievalType: "Basic",
      scoreThreshold: 0.0,
    };
    setFormData((prev) => ({
      ...prev,
      knowledgeBases: [...prev.knowledgeBases, newKB],
    }));
  };

  const handleRemoveKnowledgeBase = (id) => {
    setFormData((prev) => ({
      ...prev,
      knowledgeBases: prev.knowledgeBases.filter((kb) => kb.id !== id),
    }));
  };

  const handleKnowledgeBaseChange = (id, field, value) => {
    setFormData((prev) => {
      const selectedFolder = field === "knowledgeBase" && Array.isArray(value) && value.length
        ? findFolderById(value[0])
        : null;
      const updatedKnowledgeBases = prev.knowledgeBases.map((kb) =>
        kb.id === id ? {
          ...kb,
          [field]: value,
          ...(field === "knowledgeBase" ? { owner_user_id: selectedFolder?.owner_user_id } : {}),
        } : kb
      );

      // If changing knowledge base selection, clear any other knowledge bases that have the same folder selected
      if (field === "knowledgeBase" && Array.isArray(value) && value.length > 0) {
        const selectedFolderId = value[0];
        return {
          ...prev,
          knowledgeBases: updatedKnowledgeBases.map((kb) => {
            if (kb.id !== id && Array.isArray(kb.knowledgeBase) && kb.knowledgeBase.some((v) => String(v) === String(selectedFolderId))) {
              // Clear the selection in other knowledge bases that have the same folder
              return { ...kb, knowledgeBase: [] };
            }
            return kb;
          }),
        };
      }

      return {
        ...prev,
        knowledgeBases: updatedKnowledgeBases,
      };
    });

    // If changing retrieval type, check compatibility immediately
    if (field === "retrievalType") {
      const updatedKb = formData.knowledgeBases.find(kb => kb.id === id);
      if (updatedKb) {
        const compatibility = checkCompatibility({ ...updatedKb, [field]: value });
        if (!compatibility.compatible) {
          setErrors(prev => ({
            ...prev,
            compatibility: true,
          }));
        } else {
          setErrors(prev => ({
            ...prev,
            compatibility: false,
          }));
        }
      }
    }
  };

  const handleRecencyChange = (id, patch) => {
    setFormData((prev) => ({
      ...prev,
      knowledgeBases: prev.knowledgeBases.map((kb) =>
        kb.id === id ? applyRecencyPatch(kb, patch) : kb
      ),
    }));
  };

  const toggleCollapse = (id) => {
    setCollapsedKBs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const validateForm = () => {
    const newErrors = {
      knowledgeBase: formData.knowledgeBases.length === 0 || formData.knowledgeBases.some(kb => {
        if (Array.isArray(kb.knowledgeBase)) {
          return kb.knowledgeBase.length === 0;
        }
        return !kb.knowledgeBase || kb.knowledgeBase.trim() === "";
      }),
      numberOfChunks: formData.knowledgeBases.some(kb => kb.numberOfChunks <= 0),
      compatibility: formData.knowledgeBases.some(kb => {
        const compatibility = checkCompatibility(kb);
        return !compatibility.compatible;
      }),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    if (!personal) {
      onSave(formData);
      onClose(formData);
      return;
    }
    // A refused folder keeps the dialog open with the reason, so the user can
    // pick another one without starting over.
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(formData);
      onClose(formData);
    } catch (error) {
      setSaveError({
        message: error?.message || SHARED_KB_COPY.saveFailed,
        detail: error?.detail || "",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onClose(formData);
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Dialog
        open={open}
        onClose={() => onClose(formData)}
        maxWidth={false}
        fullWidth
        scroll="paper"
        className={styles.dialog}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(24, 24, 27, 0.4)",
              backdropFilter: "blur(2px)",
            },
          },
        }}
        PaperProps={{
          className: styles.dialogPaper,
        }}
      >
        <DialogTitle className={styles.dialogTitle} component="div">
          <Box className={styles.titleRow}>
            <Typography component="h3" variant="h6" className={styles.title}>
              {personal ? SHARED_KB_COPY.pickerTitle : "Configure Knowledge Base (D3)"}
            </Typography>
            <InfoTooltip
              title="D3 Knowledge Base Compatibility Guide"
              content={
                <Box>
                  <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                    <strong>D3 Knowledge Base Search Type + Retrieval Type Compatibility:</strong>
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                    ✅ Compatible Combinations:
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                    • Vector Search + Basic/Hybrid/MMR/HyDE
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                    • Hybrid Search + Basic/Hybrid/MMR/HyDE
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                    • Keyword Search + Basic only
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 1, color: "#f44336", fontWeight: 600 }}>
                    ❌ Incompatible:
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                    • Keyword Search + Hybrid (no embeddings)
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                    • Keyword Search + MMR (no embeddings)
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                    • Keyword Search + HyDE (no embeddings)
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                    💡 D3 Recommendation:
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                    Use Hybrid Search for maximum flexibility - supports all retrieval methods
                  </Typography>
                </Box>
              }
            >
              <IconButton size="small" className={styles.headerHelpIcon} aria-label="Compatibility help">
                <HelpCircle size={16} strokeWidth={2} />
              </IconButton>
            </InfoTooltip>
          </Box>
          <IconButton
            onClick={() => onClose(formData)}
            size="small"
            className={styles.closeButton}
            aria-label="Close"
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          <Box className={styles.formContainer}>
            {personal && (
              <Alert severity="info" sx={{ fontSize: "0.8rem" }}>
                {SHARED_KB_COPY.dim3Info}
              </Alert>
            )}
            {/* RAG Type Selection */}
            <Box className={styles.section}>
              <Box className={styles.ragTypeHeader}>
                <RadioGroup
                  value={formData.ragType}
                  onChange={(e) => handleInputChange("ragType", e.target.value)}
                  className={styles.radioGroup}
                >
                  <FormControlLabel
                    value="RAG"
                    control={<Radio size="small" />}
                    label="RAG"
                    className={styles.radioLabel}
                  />
                  <FormControlLabel
                    value="Agentic RAG"
                    control={<Radio size="small" />}
                    label="Personalize Data Source"
                    className={styles.radioLabel}
                  />
                </RadioGroup>
              </Box>
            </Box>

            {/* Knowledge Base Configuration */}
            {formData.ragType === "RAG" ? (
              <Box className={styles.section}>
                <Typography variant="body2" className={styles.sectionLabel}>
                  D3 Knowledge Base <span className={styles.required}>*</span>
                </Typography>
                <Box className={styles.knowledgeBaseContainer}>
                  <FormControl
                    fullWidth
                    size="small"
                    className={styles.selectField}
                    error={errors.knowledgeBase}
                  >
                    <InputLabel>Select D3 Folder...</InputLabel>
                    <Select
                      value={Array.isArray(formData.knowledgeBases[0]?.knowledgeBase) && formData.knowledgeBases[0].knowledgeBase.length > 0 ? findFolderById(formData.knowledgeBases[0].knowledgeBase[0])?.id ?? "" : ""}
                      onChange={(e) => {
                        const kb = formData.knowledgeBases[0] || { id: Date.now(), knowledgeBase: [], numberOfChunks: 5, retrievalType: "Basic", scoreThreshold: 0.0 };
                        handleKnowledgeBaseChange(kb.id, "knowledgeBase", [e.target.value]);
                        if (formData.knowledgeBases.length === 0) {
                          setFormData(prev => ({ ...prev, knowledgeBases: [{ ...kb, knowledgeBase: [e.target.value] }] }));
                        }
                      }}
                      label="Select D3 Folder..."
                      renderValue={(selected) => {
                        if (!selected) return "";
                        const folder = findFolderById(selected);
                        return folder ? folder.name : "";
                      }}
                      disabled={loadingFolders}
                    >
                      {loadingFolders ? (
                        <MenuItem disabled>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          Loading D3 folders...
                        </MenuItem>
                      ) : foldersError ? (
                        <MenuItem disabled>
                          <Cancel sx={{ mr: 1, color: 'error.main' }} />
                          {foldersError}
                        </MenuItem>
                      ) : folders.length === 0 ? (
                        <MenuItem disabled>
                          {personal ? SHARED_KB_COPY.noFolders : "No D3 knowledge base folders found"}
                        </MenuItem>
                      ) : (() => {
                        const availableFolders = getAvailableFolders(formData.knowledgeBases[0]?.id);
                        return availableFolders.length === 0 ? (
                          <MenuItem disabled>
                            No available D3 folders (all folders are selected in other knowledge bases)
                          </MenuItem>
                        ) : (
                          availableFolders.map((folder) => (
                            <MenuItem key={folder.id} value={folder.id} disabled={isFolderUnusable(folder) || hasEmbeddingMismatch(folder, formData.knowledgeBases[0]?.id)}>
                              <Box sx={{ flex: 1 }}>
                                <ListItemText 
                                  primary={folder.name}
                                  secondary={renderFolderInfo(folder, formData.knowledgeBases[0]?.id)}
                                  sx={{
                                    '& .MuiListItemText-primary': {
                                      fontSize: '0.875rem',
                                      fontWeight: 500
                                    },
                                    '& .MuiListItemText-secondary': {
                                      fontSize: '0.75rem'
                                    }
                                  }}
                                />
                              </Box>
                            </MenuItem>
                          ))
                        );
                      })()}
                    </Select>
                  </FormControl>
                  <IconButton 
                    size="small" 
                    className={styles.refreshButton}
                    onClick={loadFolders}
                    disabled={loadingFolders}
                  >
                    {loadingFolders ? <CircularProgress size={16} /> : <Refresh />}
                  </IconButton>
                </Box>
                <Typography 
                  variant="caption" 
                  className={styles.createNewLink}
                  onClick={() => window.open('/knowledge-hub?tab=d3', '_blank')}
                >
                  Create New D3 Folder
                  <OpenInNew fontSize="small" />
                </Typography>
                {errors.knowledgeBase && (
                  <Typography
                    variant="caption"
                    color="error"
                    className={styles.errorText}
                  >
                    Please select a D3 folder
                  </Typography>
                )}

                {/* RAG Configuration Fields */}
                <Box className={styles.section}>
                  <Typography variant="body2" className={styles.sectionLabel}>
                    Number of Chunks <span className={styles.required}>*</span>
                  </Typography>
                  <TextField
                    type="number"
                    value={formData.knowledgeBases[0]?.numberOfChunks || 15}
                    onChange={(e) => {
                      const kb = formData.knowledgeBases[0] || { id: Date.now(), knowledgeBase: "", numberOfChunks: 5, retrievalType: "Basic", scoreThreshold: 0.0 };
                      handleKnowledgeBaseChange(kb.id, "numberOfChunks", parseInt(e.target.value) || 0);
                      if (formData.knowledgeBases.length === 0) {
                        setFormData(prev => ({ ...prev, knowledgeBases: [{ ...kb, numberOfChunks: parseInt(e.target.value) || 0 }] }));
                      }
                    }}
                    size="small"
                    fullWidth
                    className={styles.textField}
                    error={errors.numberOfChunks}
                    helperText={
                      errors.numberOfChunks
                        ? "Number of chunks must be greater than 0"
                        : ""
                    }
                  />
                </Box>

                <Box className={styles.section}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" className={styles.sectionLabel}>
                      Retrieval Type <span className={styles.required}>*</span>
                    </Typography>
                    <InfoTooltip
                      title="D3 Retrieval Type Guide"
                      maxWidth={1500}
                      placement="right"
                      content={
                        <Box>
                          <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                            Choose how your D3 knowledge base will find and return relevant information:
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                            ✅ Basic Retrieval:
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            <strong>What it does:</strong> Finds the most similar documents to your question
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            <strong>Example:</strong> Ask "How to reset password?" → Returns documents about password reset steps
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                            <strong>Best for:</strong> Simple questions, works with any search type
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 1, color: "#8b5cf6", fontWeight: 600 }}>
                            🔀 Hybrid:
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            <strong>What it does:</strong> Combines meaning-based search with keyword matching
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            <strong>Example:</strong> Ask "Status of order INV-2041?" → Also matches the exact code, not just similar wording
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                            <strong>Best for:</strong> Names, codes and exact terms, requires vector search
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                            🎯 MMR (Maximal Marginal Relevance):
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            <strong>What it does:</strong> Finds relevant documents but avoids duplicates
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            <strong>Example:</strong> Ask "Benefits of exercise?" → Returns different aspects (health, mental, physical) instead of 3 similar health articles
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                            <strong>Best for:</strong> Research, comprehensive answers, requires vector search
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 1, color: "#ff9800", fontWeight: 600 }}>
                            🚀 HyDE (Hypothetical Document Embeddings):
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            <strong>What it does:</strong> Creates a "perfect answer" first, then finds similar documents
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            <strong>Example:</strong> Ask "What causes headaches?" → AI imagines ideal answer about headache causes, then finds documents matching that ideal answer
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                            <strong>Best for:</strong> Complex questions, better accuracy, requires vector search
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 1, color: "#f44336", fontWeight: 600 }}>
                            ⚠️ Important:
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                            • Hybrid, MMR and HyDE only work with Vector or Hybrid search
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                            • Basic works with all search types (Vector, Hybrid, Keyword)
                          </Typography>
                        </Box>
                      }
                    >
                      <IconButton size="small" sx={{ color: "#e36250" }}>
                        <HelpOutline sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InfoTooltip>
                  </Box>
                  
                  <FormControl
                    fullWidth
                    size="small"
                    className={`${styles.selectField} ${styles.retrievalSelect}`}
                    error={errors.compatibility}
                  >
                    <InputLabel>Select Retrieval Type...</InputLabel>
                    <Select
                      value={formData.knowledgeBases[0]?.retrievalType || "Basic"}
                      onChange={(e) => {
                        const kb = formData.knowledgeBases[0] || { id: Date.now(), knowledgeBase: "", numberOfChunks: 5, retrievalType: "Basic", scoreThreshold: 0.0 };
                        handleKnowledgeBaseChange(kb.id, "retrievalType", e.target.value);
                        if (formData.knowledgeBases.length === 0) {
                          setFormData(prev => ({ ...prev, knowledgeBases: [{ ...kb, retrievalType: e.target.value }] }));
                        }
                        
                        // Check compatibility immediately
                        const compatibility = checkCompatibility({ ...kb, retrievalType: e.target.value });
                        if (!compatibility.compatible) {
                          setErrors(prev => ({ ...prev, compatibility: true }));
                        } else {
                          setErrors(prev => ({ ...prev, compatibility: false }));
                        }
                      }}
                      label="Select Retrieval Type..."
                    >
                      {renderRetrievalTypeOptions()}
                    </Select>
                  </FormControl>
                  
                  {/* Compatibility Status */}
                  {formData.knowledgeBases[0]?.knowledgeBase && formData.knowledgeBases[0].knowledgeBase.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {(() => {
                        const compatibility = checkCompatibility(formData.knowledgeBases[0]);
                        return (
                          <Alert
                            severity={compatibility.compatible ? "success" : "error"}
                            sx={{ 
                              fontSize: "0.75rem",
                              "& .MuiAlert-message": { fontSize: "0.75rem" }
                            }}
                            icon={compatibility.compatible ? <CheckCircle /> : <Warning />}
                          >
                            {compatibility.message}
                          </Alert>
                        );
                      })()}
                    </Box>
                  )}
                </Box>

                <KnowledgeBaseRecencyFields
                  kb={formData.knowledgeBases[0]}
                  onChange={(patch) => handleRecencyChange(formData.knowledgeBases[0]?.id, patch)}
                  styles={styles}
                  InfoTooltip={InfoTooltip}
                />

                <Box className={styles.section}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" className={styles.sectionLabel}>
                      Score Threshold: {formData.knowledgeBases[0]?.scoreThreshold ?? 0}{" "}
                      <span className={styles.required}>*</span>
                    </Typography>
                    <InfoTooltip
                      title="D3 Score Threshold Guide"
                      maxWidth={800}
                      placement="right"
                      content={
                        <Box>
                          <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                            <strong>What it does:</strong> Filters out documents that are not similar enough to your question
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                            📊 How it works:
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            • 0.0 = Show all documents (even if not very relevant)
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            • 0.5 = Show only moderately relevant documents
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            • 0.8 = Show only highly relevant documents
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                            • 1.0 = Show only perfectly matching documents
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                            💡 Examples:
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            • <strong>Low (0.0-0.3):</strong> "Tell me about cars" → Returns all car-related content, even loosely related
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            • <strong>Medium (0.4-0.6):</strong> "How to change oil?" → Returns car maintenance guides, filters out general car info
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                            • <strong>High (0.7-1.0):</strong> "BMW oil change procedure" → Returns only specific BMW oil change instructions
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 1, color: "#ff9800", fontWeight: 600 }}>
                            ⚠️ Recommendations:
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                            • Start with 0.0-0.3 for general questions
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                            • Use 0.4-0.6 for specific technical questions
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                            • Use 0.7+ only when you need very precise answers
                          </Typography>
                        </Box>
                      }
                    >
                      <IconButton size="small" sx={{ color: "#e36250" }}>
                        <HelpOutline sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InfoTooltip>
                  </Box>
                  <Box className={styles.sliderContainer}>
                    <Slider
                      value={formData.knowledgeBases[0]?.scoreThreshold || 0.0}
                      onChange={(e, value) => {
                        const kb = formData.knowledgeBases[0] || { id: Date.now(), knowledgeBase: "", numberOfChunks: 5, retrievalType: "Basic", scoreThreshold: 0.0 };
                        handleKnowledgeBaseChange(kb.id, "scoreThreshold", value);
                        if (formData.knowledgeBases.length === 0) {
                          setFormData(prev => ({ ...prev, knowledgeBases: [{ ...kb, scoreThreshold: value }] }));
                        }
                      }}
                      min={0}
                      max={1}
                      step={0.1}
                      valueLabelDisplay="auto"
                      className={styles.slider}
                    />
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box className={styles.section}>
                <Button
                  variant="contained"
                  onClick={handleAddKnowledgeBase}
                  className={styles.addKnowledgeBaseButton}
                  startIcon={<OpenInNew />}
                >
                  Add D3 Knowledgebase
                </Button>

                {formData.knowledgeBases.map((kb, index) => {
                  const isCollapsed = collapsedKBs.has(kb.id);
                  const hasKnowledgeBaseError = Array.isArray(kb.knowledgeBase) ? kb.knowledgeBase.length === 0 : (!kb.knowledgeBase || kb.knowledgeBase.trim() === "");
                  const hasChunksError = kb.numberOfChunks <= 0;
                  
                  return (
                    <Box key={kb.id} className={styles.knowledgeBaseItem}>
                      <Box className={styles.kbItemHeader}>
                        <Typography variant="body2" className={styles.kbItemTitle}>
                          D3 Knowledge Base {index + 1}
                        </Typography>
                        <Box className={styles.kbItemActions}>
                          <IconButton 
                            size="small" 
                            className={styles.expandButton}
                            onClick={() => toggleCollapse(kb.id)}
                          >
                            {isCollapsed ? <ExpandMore /> : <ExpandLess />}
                          </IconButton>
                          <IconButton 
                            size="small" 
                            className={styles.removeButton}
                            onClick={() => handleRemoveKnowledgeBase(kb.id)}
                          >
                            <Remove />
                          </IconButton>
                        </Box>
                      </Box>

                      {!isCollapsed && (
                        <Box className={styles.kbItemContent}>
                          <Box className={styles.section}>
                            <Typography variant="body2" className={styles.sectionLabel}>
                              D3 Knowledge Base <span className={styles.required}>*</span>
                            </Typography>
                            <FormControl
                              fullWidth
                              size="small"
                              className={styles.selectField}
                              error={hasKnowledgeBaseError}
                            >
                              <InputLabel>Select D3 Folder...</InputLabel>
                              <Select
                                value={Array.isArray(kb.knowledgeBase) && kb.knowledgeBase.length > 0 ? findFolderById(kb.knowledgeBase[0])?.id ?? "" : ""}
                                onChange={(e) =>
                                  handleKnowledgeBaseChange(kb.id, "knowledgeBase", [e.target.value])
                                }
                                label="Select D3 Folder..."
                                renderValue={(selected) => {
                                  if (!selected) return "";
                                  const folder = findFolderById(selected);
                                  return folder ? folder.name : "";
                                }}
                                disabled={loadingFolders}
                              >
                                {loadingFolders ? (
                                  <MenuItem disabled>
                                    <CircularProgress size={16} sx={{ mr: 1 }} />
                                    Loading D3 folders...
                                  </MenuItem>
                                ) : foldersError ? (
                                  <MenuItem disabled>
                                    <Cancel sx={{ mr: 1, color: 'error.main' }} />
                                    {foldersError}
                                  </MenuItem>
                                ) : folders.length === 0 ? (
                                  <MenuItem disabled>
                                    {personal ? SHARED_KB_COPY.noFolders : "No D3 knowledge base folders found"}
                                  </MenuItem>
                                ) : (() => {
                                  const availableFolders = getAvailableFolders(kb.id);
                                  return availableFolders.length === 0 ? (
                                    <MenuItem disabled>
                                      No available D3 folders (all folders are selected in other knowledge bases)
                                    </MenuItem>
                                  ) : (
                                    availableFolders.map((folder) => (
                                      <MenuItem key={folder.id} value={folder.id} disabled={isFolderUnusable(folder) || hasEmbeddingMismatch(folder, kb.id)}>
                                        <Box sx={{ flex: 1 }}>
                                          <ListItemText 
                                            primary={folder.name}
                                            secondary={renderFolderInfo(folder, kb.id)}
                                            sx={{
                                              '& .MuiListItemText-primary': {
                                                fontSize: '0.875rem',
                                                fontWeight: 500
                                              },
                                              '& .MuiListItemText-secondary': {
                                                fontSize: '0.75rem'
                                              }
                                            }}
                                          />
                                        </Box>
                                      </MenuItem>
                                    ))
                                  );
                                })()}
                              </Select>
                            </FormControl>
                            {hasKnowledgeBaseError && (
                              <Typography
                                variant="caption"
                                color="error"
                                className={styles.errorText}
                              >
                                Please select a D3 folder
                              </Typography>
                            )}
                          </Box>

                          <Box className={styles.section}>
                            <Typography variant="body2" className={styles.sectionLabel}>
                              Number of Chunks <span className={styles.required}>*</span>
                            </Typography>
                            <TextField
                              type="number"
                              value={kb.numberOfChunks}
                              onChange={(e) =>
                                handleKnowledgeBaseChange(kb.id, "numberOfChunks", parseInt(e.target.value) || 0)
                              }
                              size="small"
                              fullWidth
                              className={styles.textField}
                              error={hasChunksError}
                              helperText={
                                hasChunksError
                                  ? "Number of chunks must be greater than 0"
                                  : ""
                              }
                            />
                          </Box>

                          <Box className={styles.section}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="body2" className={styles.sectionLabel}>
                                Retrieval Type <span className={styles.required}>*</span>
                              </Typography>
                              <InfoTooltip
                                title="D3 Retrieval Type Guide"
                                maxWidth={1500}
                                placement="right"
                                content={
                                  <Box>
                                    <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                                      Choose how your D3 knowledge base will find and return relevant information:
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                                      ✅ Basic Retrieval:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      <strong>What it does:</strong> Finds the most similar documents to your question
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      <strong>Example:</strong> Ask "How to reset password?" → Returns documents about password reset steps
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                                      <strong>Best for:</strong> Simple questions, works with any search type
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mb: 1, color: "#8b5cf6", fontWeight: 600 }}>
                                      🔀 Hybrid:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      <strong>What it does:</strong> Combines meaning-based search with keyword matching
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      <strong>Example:</strong> Ask "Status of order INV-2041?" → Also matches the exact code, not just similar wording
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                                      <strong>Best for:</strong> Names, codes and exact terms, requires vector search
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                                      🎯 MMR (Maximal Marginal Relevance):
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      <strong>What it does:</strong> Finds relevant documents but avoids duplicates
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      <strong>Example:</strong> Ask "Benefits of exercise?" → Returns different aspects (health, mental, physical) instead of 3 similar health articles
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                                      <strong>Best for:</strong> Research, comprehensive answers, requires vector search
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mb: 1, color: "#ff9800", fontWeight: 600 }}>
                                      🚀 HyDE (Hypothetical Document Embeddings):
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      <strong>What it does:</strong> Creates a "perfect answer" first, then finds similar documents
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      <strong>Example:</strong> Ask "What causes headaches?" → AI imagines ideal answer about headache causes, then finds documents matching that ideal answer
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                                      <strong>Best for:</strong> Complex questions, better accuracy, requires vector search
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mb: 1, color: "#f44336", fontWeight: 600 }}>
                                      ⚠️ Important:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                      • Hybrid, MMR and HyDE only work with Vector or Hybrid search
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                                      • Basic works with all search types (Vector, Hybrid, Keyword)
                                    </Typography>
                                  </Box>
                                }
                              >
                                <IconButton size="small" sx={{ color: "#e36250" }}>
                                  <HelpOutline sx={{ fontSize: 18 }} />
                                </IconButton>
                              </InfoTooltip>
                            </Box>
                            
                            <FormControl
                              fullWidth
                              size="small"
                              className={`${styles.selectField} ${styles.retrievalSelect}`}
                              error={errors.compatibility}
                            >
                              <InputLabel>Select Retrieval Type...</InputLabel>
                              <Select
                                value={kb.retrievalType}
                                onChange={(e) =>
                                  handleKnowledgeBaseChange(kb.id, "retrievalType", e.target.value)
                                }
                                label="Select Retrieval Type..."
                              >
                                {renderRetrievalTypeOptions()}
                              </Select>
                            </FormControl>
                            
                            {/* Compatibility Status */}
                            {kb.knowledgeBase && kb.knowledgeBase.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                {(() => {
                                  const compatibility = checkCompatibility(kb);
                                  return (
                                    <Alert
                                      severity={compatibility.compatible ? "success" : "error"}
                                      sx={{ 
                                        fontSize: "0.75rem",
                                        "& .MuiAlert-message": { fontSize: "0.75rem" }
                                      }}
                                      icon={compatibility.compatible ? <CheckCircle /> : <Warning />}
                                    >
                                      {compatibility.message}
                                    </Alert>
                                  );
                                })()}
                              </Box>
                            )}
                          </Box>

                          <KnowledgeBaseRecencyFields
                            kb={kb}
                            onChange={(patch) => handleRecencyChange(kb.id, patch)}
                            styles={styles}
                            InfoTooltip={InfoTooltip}
                          />

                          <Box className={styles.section}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="body2" className={styles.sectionLabel}>
                                Score Threshold: {kb.scoreThreshold}{" "}
                                <span className={styles.required}>*</span>
                              </Typography>
                              <InfoTooltip
                                title="D3 Score Threshold Guide"
                                maxWidth={800}
                                placement="right"
                                content={
                                  <Box>
                                    <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                                      <strong>What it does:</strong> Filters out documents that are not similar enough to your question
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                                      📊 How it works:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      • 0.0 = Show all documents (even if not very relevant)
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      • 0.5 = Show only moderately relevant documents
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      • 0.8 = Show only highly relevant documents
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                                      • 1.0 = Show only perfectly matching documents
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                                      💡 Examples:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      • <strong>Low (0.0-0.3):</strong> "Tell me about cars" → Returns all car-related content, even loosely related
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      • <strong>Medium (0.4-0.6):</strong> "How to change oil?" → Returns car maintenance guides, filters out general car info
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                                      • <strong>High (0.7-1.0):</strong> "BMW oil change procedure" → Returns only specific BMW oil change instructions
                                    </Typography>
                                    
                                    <Typography variant="body2" sx={{ mb: 1, color: "#ff9800", fontWeight: 600 }}>
                                      ⚠️ Recommendations:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                      • Start with 0.0-0.3 for general questions
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                      • Use 0.4-0.6 for specific technical questions
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                                      • Use 0.7+ only when you need very precise answers
                                    </Typography>
                                  </Box>
                                }
                              >
                                <IconButton size="small" sx={{ color: "#e36250" }}>
                                  <HelpOutline sx={{ fontSize: 18 }} />
                                </IconButton>
                              </InfoTooltip>
                            </Box>
                            <Box className={styles.sliderContainer}>
                              <Slider
                                value={kb.scoreThreshold}
                                onChange={(e, value) =>
                                  handleKnowledgeBaseChange(kb.id, "scoreThreshold", value)
                                }
                                min={0}
                                max={1}
                                step={0.1}
                                valueLabelDisplay="auto"
                                className={styles.slider}
                              />
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
            {saveError && (
              <Alert severity="error" onClose={() => setSaveError(null)}>
                {saveError.message}
                {saveError.detail && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {saveError.detail}
                  </Typography>
                )}
              </Alert>
            )}
          </Box>

        </DialogContent>

        <DialogActions className={styles.dialogActions}>
          <Button
            onClick={handleCancel}
            variant="text"
            className={styles.cancelButton}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            className={styles.saveButton}
            disabled={errors.compatibility || saving}
          >
            {errors.compatibility ? "Fix Compatibility Issues" : saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default KnowledgeBaseD3Dialog;
